<template>
  <div class="profile-page">
    <div class="container">
      <h1>My Profile</h1>

      <Spinner :show="loading" message="Loading your profile..." />

      <template v-if="!loading">
        <!-- Your Voice -->
        <section class="profile-section">
          <h2>Your Voice</h2>
          <div v-if="quizJustCompleted" class="quiz-success">
            Saved! Here's what we picked up.
          </div>
          <div v-if="profile && profile.identity" class="voice-details">
            <div class="trait-grid">
              <div class="trait" v-if="profile.identity.humor">
                <span class="trait-label">Humor</span>
                <span class="trait-value">{{ profile.identity.humor }}</span>
              </div>
              <div class="trait" v-if="profile.identity.warmth">
                <span class="trait-label">Warmth</span>
                <span class="trait-value">{{ profile.identity.warmth }}</span>
              </div>
              <div class="trait" v-if="profile.identity.energy">
                <span class="trait-label">Energy</span>
                <span class="trait-value">{{ profile.identity.energy }}</span>
              </div>
              <div class="trait" v-if="profile.identity.formality">
                <span class="trait-label">Formality</span>
                <span class="trait-value">{{ profile.identity.formality }}</span>
              </div>
            </div>
            <div v-if="profile.identity.voiceGoals && profile.identity.voiceGoals.length" class="voice-goals">
              <strong>Voice goals:</strong>
              <span v-for="(goal, i) in profile.identity.voiceGoals" :key="i" class="goal-chip">{{ goal }}</span>
            </div>
            <p v-if="profile.identity.selfNote" class="self-note">"{{ profile.identity.selfNote }}"</p>
          </div>
          <p v-else class="empty-state">Take the quiz to help us learn your voice.</p>
          <button class="button secondary" @click="showQuiz = true">
            {{ profile && profile.identity ? 'Retake Quiz' : 'Take Quiz' }}
          </button>
        </section>

        <!-- What You Pick -->
        <section class="profile-section" v-if="profile && hasAnyAffinityData">
          <h2>What You Pick</h2>
          <div class="affinities">
            <div v-if="rankedStyles.length" class="affinity-group">
              <h3>Favorite Styles</h3>
              <div class="affinity-list">
                <div v-for="item in rankedStyles" :key="item.name" class="affinity-row">
                  <span class="affinity-name">{{ item.name }}</span>
                  <div class="affinity-bar-track">
                    <div class="affinity-bar-fill" :style="{ width: item.pct + '%' }"></div>
                  </div>
                  <span class="affinity-stat">{{ item.chosen }}/{{ item.total }}</span>
                </div>
              </div>
            </div>
            <div v-if="rankedTones.length" class="affinity-group">
              <h3>Favorite Tones</h3>
              <div class="affinity-list">
                <div v-for="item in rankedTones" :key="item.name" class="affinity-row">
                  <span class="affinity-name">{{ item.name }}</span>
                  <div class="affinity-bar-track">
                    <div class="affinity-bar-fill" :style="{ width: item.pct + '%' }"></div>
                  </div>
                  <span class="affinity-stat">{{ item.chosen }}/{{ item.total }}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Your People -->
        <section class="profile-section">
          <h2>Your People</h2>
          <p v-if="relationshipCount > 0">
            You have <strong>{{ relationshipCount }}</strong> {{ relationshipCount === 1 ? 'person' : 'people' }} saved.
          </p>
          <p v-else class="empty-state">No people added yet.</p>
          <router-link to="/my-people" class="button secondary">
            {{ relationshipCount > 0 ? 'View My People' : 'Add Someone' }}
          </router-link>
        </section>

        <!-- Your Assistant -->
        <section class="profile-section">
          <h2>Your Assistant</h2>

          <!-- Membership is the switch that turns the assistant on. -->
          <div v-if="isMember" class="membership-banner on">
            <span class="mb-pill">Membership active</span>
            <span class="mb-text">You're set — your assistant turns on for you as we roll it out.</span>
          </div>
          <router-link v-else to="/membership" class="membership-banner cta">
            <span class="mb-text"><strong>Turn your assistant on</strong> — a membership lets it watch your people and reach out for you.</span>
            <span class="mb-arrow">Become a member &rarr;</span>
          </router-link>

          <p class="section-intro">
            When it's on, your assistant keeps an eye on the people you've added —
            sending a note when you've gone quiet, or suggesting a gift before a big date.
            You approve every gift before anything is made or mailed.
          </p>
          <router-link to="/assistant" class="button secondary assistant-link">See what it's thinking &rarr;</router-link>

          <ul class="readiness-list">
            <li v-for="item in readinessItems" :key="item.key" :class="{ done: item.done }">
              <span class="readiness-mark" aria-hidden="true">{{ item.done ? '✓' : '○' }}</span>
              <span class="sr-only">{{ item.done ? 'Done: ' : 'Not done yet: ' }}</span>
              <span class="readiness-text">
                {{ item.label }}
                <span v-if="item.hint" class="readiness-hint">{{ item.hint }}</span>
              </span>
            </li>
          </ul>

          <p v-if="isMember && agentReady" class="readiness-summary ready">
            You're all set — we'll let you know the moment your assistant starts reaching out.
          </p>
          <p v-else-if="isMember" class="readiness-summary">
            Finish these and your assistant will have everything it needs.
          </p>
          <p v-else class="readiness-summary">
            These get you ready — become a member to switch the assistant on.
          </p>

          <button class="button secondary" :disabled="sendingDigest" @click="sendDigest">
            {{ sendingDigest ? 'Sending…' : 'Email me my relationship digest' }}
          </button>

          <div class="agent-activity">
            <h3>Recent activity</h3>
            <ul v-if="agentActions.length" class="activity-list">
              <li v-for="a in agentActions" :key="a.id" class="activity-row">
                <span class="activity-dot" :class="actionStatus(a).cls" aria-hidden="true">{{ actionStatus(a).icon }}</span>
                <div class="activity-body">
                  <div class="activity-title">{{ a.recipientName || 'Someone' }} — {{ actionStatus(a).label }}</div>
                  <div class="activity-sub">
                    <span v-if="actionReason(a)" class="activity-reason">{{ actionReason(a) }}</span>
                    <span class="activity-time">{{ timeAgo(a.createdAt) }}</span>
                    <a
                      v-if="canCaptureReaction(a) && reactionFor !== a.id"
                      href="#"
                      class="reaction-link"
                      @click.prevent="reactionFor = a.id; reactionText = ''"
                    >How'd it land?</a>
                  </div>
                  <div v-if="reactionFor === a.id" class="reaction-form">
                    <input
                      type="text"
                      v-model="reactionText"
                      maxlength="300"
                      :placeholder="`what ${a.recipientName || 'they'} said back, or anything new with them`"
                      class="reaction-input"
                      @keyup.enter="saveReaction(a)"
                    />
                    <button class="button small primary" :disabled="!reactionText.trim() || savingReaction" @click="saveReaction(a)">Save</button>
                    <button class="button small secondary" @click="reactionFor = null">Cancel</button>
                  </div>
                </div>
              </li>
            </ul>
            <p v-else class="empty-state">Nothing yet — when your assistant reaches out, you'll see it here.</p>
          </div>
        </section>

        <!-- Account -->
        <section class="profile-section">
          <h2>Account</h2>
          <p class="account-email">{{ userEmail }}</p>
          <button class="button logout" @click="handleLogout">Log Out</button>
        </section>
      </template>
    </div>

    <!-- Quiz Modal -->
    <ModalView v-if="showQuiz" @close="showQuiz = false">
      <template #header>
        <h3>Voice Quiz</h3>
      </template>
      <template #body>
        <SenderOnboardingQuiz @complete="onQuizComplete" @cancel="showQuiz = false" />
      </template>
    </ModalView>
  </div>
</template>

<script>
import { auth, db } from '@/firebase';
import { signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { mapActions, mapGetters } from 'vuex';
import ModalView from '@/components/ModalView.vue';
import SenderOnboardingQuiz from '@/components/SenderOnboardingQuiz.vue';
import Spinner from '@/components/SpinnerComp.vue';
import { sendKnowledgeGraphDigest, getSubscriptionDetails } from '@/api';
import { isContactable } from '@/utils/agentEligibility';
import { isActiveMembershipStatus } from '@/config/membership';

const ACTION_STATUS = {
  generating: { cls: 'pending', icon: '✎', label: 'preparing a note' },
  delivering: { cls: 'pending', icon: '➤', label: 'sending a note' },
  delivered: { cls: 'ok', icon: '✓', label: 'sent a note' },
  sandboxed: { cls: 'pending', icon: '🧪', label: 'previewed a note to you (sandbox — nothing was sent)' },
  refused: { cls: 'pending', icon: '✋', label: "held a note back (it didn't pass our content check)" },
  failed: { cls: 'failed', icon: '⚠️', label: "a note didn't go through" },
};

export default {
  name: 'MyProfilePage',
  components: { ModalView, SenderOnboardingQuiz, Spinner },
  data() {
    return {
      loading: true,
      showQuiz: false,
      quizJustCompleted: false,
      relationshipCount: 0,
      contactableCount: 0,
      agentActions: [],
      sendingDigest: false,
      membershipStatus: null,
      userEmail: '',
      reactionFor: null,
      reactionText: '',
      savingReaction: false,
    };
  },
  computed: {
    ...mapGetters(['senderProfile']),
    profile() {
      return this.senderProfile;
    },
    isMember() {
      return isActiveMembershipStatus(this.membershipStatus);
    },
    hasAnyAffinityData() {
      if (!this.profile) return false;
      const hasStyle = this.rankedStyles.length > 0;
      const hasTone = this.rankedTones.length > 0;
      return hasStyle || hasTone;
    },
    rankedStyles() {
      // Affinities live under `voice`, not `identity` (the backend writes them there).
      return this.rankAffinities(this.profile?.voice?.styleAffinities);
    },
    rankedTones() {
      return this.rankAffinities(this.profile?.voice?.toneAffinities);
    },
    quizDone() {
      return !!(this.profile && this.profile.identity && this.profile.identity.quizCompletedAt);
    },
    postcardsSent() {
      return (this.profile && this.profile.cadence && this.profile.cadence.totalPostcardsSent) || 0;
    },
    enoughPeople() {
      return this.contactableCount >= 3;
    },
    hasSentPostcard() {
      return this.postcardsSent >= 1;
    },
    agentReady() {
      return this.quizDone && this.enoughPeople && this.hasSentPostcard;
    },
    readinessItems() {
      return [
        {
          key: 'quiz',
          done: this.quizDone,
          label: 'Tell us your voice',
          hint: this.quizDone ? '' : 'Take the voice quiz above',
        },
        {
          key: 'people',
          done: this.enoughPeople,
          label: 'Add 3 reachable people',
          hint: this.enoughPeople
            ? ''
            : `${this.contactableCount} of 3 — someone is "reachable" once they have a consented email or mailing address`,
        },
        {
          key: 'sent',
          done: this.hasSentPostcard,
          label: 'Send at least one postcard',
          hint: this.hasSentPostcard ? '' : 'Create and send a postcard first',
        },
      ];
    },
  },
  async mounted() {
    const user = auth.currentUser;
    if (!user) return;

    this.userEmail = user.email;

    // Check for welcome mode
    if (this.$route.query.welcome === 'true') {
      this.showQuiz = true;
    }

    await this.loadProfile(user.uid);
    this.loading = false;
  },
  methods: {
    ...mapActions(['setSenderProfile', 'logout']),
    rankAffinities(affinities) {
      if (!affinities) return [];
      // The backend writes `chosen` (and `rejected` for tones), never `total`,
      // so derive the denominator here — otherwise every row filters out.
      return Object.entries(affinities)
        .map(([name, data]) => {
          const chosen = data.chosen || 0;
          const rejected = data.rejected || 0;
          const total = chosen + rejected;
          return {
            name,
            chosen,
            total,
            pct: total > 0 ? Math.round((chosen / total) * 100) : 0,
          };
        })
        .filter((item) => item.chosen > 0)
        .sort((a, b) => b.chosen - a.chosen);
    },
    async loadProfile(uid) {
      try {
        const profileDoc = await getDoc(doc(db, 'senderProfiles', uid));
        if (profileDoc.exists()) {
          this.setSenderProfile(profileDoc.data());
        }

        const relQuery = query(
          collection(db, 'relationships'),
          where('userId', '==', uid),
          where('status', '==', 'active')
        );
        const relSnap = await getDocs(relQuery);
        const rels = relSnap.docs.map((d) => d.data());
        this.relationshipCount = rels.length;
        this.contactableCount = rels.filter(isContactable).length;

        // Recent assistant activity — owner-readable; where(userId==) only, so
        // no composite index is needed (sorted + capped client-side).
        const actionsSnap = await getDocs(
          query(collection(db, 'agentActions'), where('userId', '==', uid))
        );
        this.agentActions = actionsSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
          .slice(0, 8);

        // Membership status drives whether the assistant is actually on.
        try {
          const sub = await getSubscriptionDetails();
          this.membershipStatus = (sub && sub.data && sub.data.subscriptionDetails &&
            sub.data.subscriptionDetails.status) || null;
        } catch (subErr) {
          this.membershipStatus = null;
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    },
    async onQuizComplete() {
      this.showQuiz = false;
      // Reload to get fresh data
      const user = auth.currentUser;
      if (user) {
        await this.loadProfile(user.uid);
      }
      this.quizJustCompleted = true;
      setTimeout(() => {
        this.quizJustCompleted = false;
      }, 5000);
    },
    async sendDigest() {
      this.sendingDigest = true;
      try {
        const res = await sendKnowledgeGraphDigest();
        if (res && res.data && res.data.success) {
          this.$toast.success('Sent! Check your inbox for your relationship digest.');
        } else {
          this.$toast.error((res && res.data && res.data.message) || "Couldn't send your digest.");
        }
      } catch (error) {
        this.$toast.error("Couldn't send your digest — please try again.");
      } finally {
        this.sendingDigest = false;
      }
    },
    actionStatus(action) {
      // A refused action is stored as status 'failed' + refused:true so
      // existing consumers keep working; the feed tells the two apart.
      const key = action.status === 'failed' && action.refused ? 'refused' : action.status;
      return ACTION_STATUS[key] || { cls: 'pending', icon: '•', label: 'took action' };
    },
    actionReason(action) {
      const r = action.reasoning || {};
      if (r.significantDate && r.significantDate.occasion) return `For their ${r.significantDate.occasion}`;
      if (r.significantDate && r.significantDate.type) return `For their ${r.significantDate.type}`;
      return r.explanation || '';
    },
    // A recent delivered action is the natural moment to capture how it
    // landed — the reply/reaction becomes a fresh signal on the relationship.
    canCaptureReaction(a) {
      if (!a.relationshipId || !a.createdAt) return false;
      const recent = Date.now() - a.createdAt < 14 * 86400000;
      return recent && a.status === 'delivered';
    },
    async saveReaction(a) {
      const text = this.reactionText.trim();
      if (!text || this.savingReaction) return;
      this.savingReaction = true;
      try {
        const relRef = doc(db, 'relationships', a.relationshipId);
        const relSnap = await getDoc(relRef);
        if (relSnap.exists()) {
          const prior = (relSnap.data().signals || []).slice(-19);
          await updateDoc(relRef, {
            signals: [...prior, { text, at: Date.now() }],
            updatedAt: Date.now(),
          });
        }
        this.reactionFor = null;
        this.reactionText = '';
        this.$toast.success('Noted — this makes the next one better.');
      } catch {
        this.$toast.error('Failed to save.');
      } finally {
        this.savingReaction = false;
      }
    },
    timeAgo(timestamp) {
      if (!timestamp) return '';
      const days = Math.floor((Date.now() - timestamp) / 86400000);
      if (days > 30) return `${Math.floor(days / 30)} month${Math.floor(days / 30) === 1 ? '' : 's'} ago`;
      if (days > 0) return `${days} day${days === 1 ? '' : 's'} ago`;
      const hours = Math.floor((Date.now() - timestamp) / 3600000);
      if (hours > 0) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
      return 'just now';
    },
    async handleLogout() {
      try {
        await signOut(auth);
        this.logout();
        this.$router.push('/');
      } catch (error) {
        console.error('Error logging out:', error);
      }
    },
  },
};
</script>

<style scoped>
.profile-page {
  padding: 50px 20px;
  min-height: 100vh;
  background-color: var(--light-pink);
}

.container {
  max-width: 640px;
  margin: 0 auto;
}

h1 {
  color: var(--hollywood-cerise);
  font-size: 2rem;
  margin-bottom: 30px;
  text-align: center;
}

.profile-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.profile-section h2 {
  color: var(--hollywood-cerise);
  font-size: 1.25rem;
  margin: 0 0 16px;
}

.empty-state {
  color: #999;
  font-style: italic;
  margin-bottom: 12px;
}

.quiz-success {
  background-color: #e8f5e9;
  color: #2e7d32;
  padding: 10px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 16px;
  animation: fade-in 0.3s ease;
}

@keyframes fade-in {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}

.trait-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.trait {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #f8f0f4;
  border-radius: 10px;
  padding: 10px 16px;
  min-width: 80px;
}

.trait-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #999;
  margin-bottom: 4px;
}

.trait-value {
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--hollywood-cerise);
  text-transform: capitalize;
}

.voice-goals {
  margin-bottom: 12px;
  font-size: 0.9rem;
}

.goal-chip {
  display: inline-block;
  background-color: #ffe6ed;
  color: var(--hollywood-cerise);
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 0.8rem;
  margin: 4px 4px 4px 0;
}

.self-note {
  color: #666;
  font-style: italic;
  font-size: 0.9rem;
  margin-bottom: 12px;
}

.affinities {
  margin-bottom: 8px;
}

.affinity-group {
  margin-bottom: 16px;
}

.affinity-group:last-child {
  margin-bottom: 0;
}

.affinity-group h3 {
  font-size: 0.95rem;
  color: var(--rich-black);
  margin: 0 0 10px;
}

.affinity-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.affinity-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.affinity-name {
  min-width: 90px;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--rich-black);
}

.affinity-bar-track {
  flex: 1;
  height: 8px;
  background-color: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
}

.affinity-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--hollywood-cerise), var(--accent2-color));
  border-radius: 4px;
  transition: width 0.4s ease;
  min-width: 2px;
}

.affinity-stat {
  min-width: 36px;
  font-size: 0.8rem;
  color: #999;
  text-align: right;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.membership-banner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 12px 14px;
  border-radius: 10px;
  margin-bottom: 14px;
  font-size: 0.9rem;
  text-decoration: none;
}

.membership-banner.on {
  background: #e6f7ec;
  color: #1e7a44;
}

.membership-banner.cta {
  background: linear-gradient(135deg, #ffe6ed, #ede7ff);
  border: 1px solid #ffd0de;
  color: var(--rich-black);
  justify-content: space-between;
  transition: box-shadow 0.2s, transform 0.1s;
}

.membership-banner.cta:hover {
  box-shadow: 0 4px 12px rgba(214, 51, 132, 0.18);
  transform: translateY(-1px);
}

.mb-pill {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: #1e7a44;
  color: #fff;
  padding: 3px 9px;
  border-radius: 10px;
}

.mb-text { flex: 1; min-width: 180px; line-height: 1.4; }
.mb-arrow { color: var(--hollywood-cerise); font-weight: 700; white-space: nowrap; }

.section-intro {
  color: #666;
  font-size: 0.92rem;
  line-height: 1.5;
  margin: 0 0 16px;
}

.readiness-list {
  list-style: none;
  padding: 0;
  margin: 0 0 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.readiness-list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #999;
}

.readiness-list li.done {
  color: var(--rich-black);
}

.readiness-mark {
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  background-color: #f0f0f0;
  color: #bbb;
}

.readiness-list li.done .readiness-mark {
  background-color: #e6f7ec;
  color: #1e7a44;
}

.readiness-text {
  font-size: 0.92rem;
  font-weight: 500;
}

.readiness-hint {
  display: block;
  font-size: 0.8rem;
  font-weight: 400;
  color: #999;
  margin-top: 2px;
}

.readiness-summary {
  font-size: 0.9rem;
  color: #777;
  margin: 0 0 16px;
}

.readiness-summary.ready {
  color: #1e7a44;
  font-weight: 600;
}

.agent-activity {
  margin-top: 20px;
  border-top: 1px solid #f0f0f0;
  padding-top: 16px;
}

.agent-activity h3 {
  font-size: 0.95rem;
  color: var(--rich-black);
  margin: 0 0 12px;
}

.activity-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.activity-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.activity-dot {
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  background-color: #f1f1f1;
}

.activity-dot.ok { background-color: #e6f7ec; }
.activity-dot.failed { background-color: #fdeaea; }
.activity-dot.pending { background-color: #fff2df; }

.activity-body {
  flex: 1;
  min-width: 0;
}

.activity-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--rich-black);
}

.activity-sub {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 0.8rem;
  color: #888;
  margin-top: 2px;
}

.reaction-link {
  color: var(--hollywood-cerise);
  text-decoration: none;
  font-weight: 600;
}

.reaction-link:hover { text-decoration: underline; }

.reaction-form {
  display: flex;
  gap: 6px;
  margin-top: 6px;
  align-items: center;
}

.reaction-input {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.85rem;
}

.reaction-input:focus { outline: none; border-color: var(--hollywood-cerise); }

.activity-reason {
  color: var(--hollywood-cerise);
}

.account-email {
  color: var(--rich-black);
  margin-bottom: 12px;
}

.button {
  display: inline-block;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  text-align: center;
  transition: background-color 0.3s;
}

.button.secondary {
  background-color: var(--hollywood-cerise);
  color: white;
}

.button.secondary:hover {
  background-color: var(--violet-lush);
}

.button.secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.button.logout {
  background-color: #eee;
  color: var(--rich-black);
}

.button.logout:hover {
  background-color: #ddd;
}

@media (max-width: 480px) {
  .profile-page {
    padding: 30px 10px;
  }

  h1 {
    font-size: 1.7rem;
  }

  .profile-section {
    padding: 18px;
  }
}
</style>
